import { useState, type ReactElement } from "react";
import type { Submission } from "../hooks/useSubmissions";
import { CardPreviews } from "../pages/ReviewStep/CardPreviews";
import { TitlePreview } from "../pages/ReviewStep/TitlePreview";
import { formDataToPreview, submissionToPreview } from "../utils/previewHelpers";
import { BasicInfoSection, ContentDetailsSection, CreditsSection, VisualAssetsSection } from "./ViewSubmissionSections";

/* eslint-disable max-lines-per-function, max-lines */
// NOTE: Shared review display component with multiple view modes

interface ContentReviewDisplayProps {
  // Either submission data or form data
  submission?: Submission;
  formData?: {
    title?: string;
    releaseYear?: number;
    runtime?: number;
    synopsis?: string;
    description?: string;
    director?: string;
    cast?: string[];
    crew?: Array<{ name: string; job: string; department?: string }>;
    genres?: string[];
    rating?: string;
    language?: string | string[]; // Primary field used by forms (supports multi-select)
    languages?: string[]; // Deprecated - fallback for backward compatibility
    country?: string;
    posterUrl?: string;
    backdropUrl?: string;
    logoUrl?: string;
    horizontalTileUrl?: string;
    verticalTileUrl?: string;
    featureVideoUrl?: string;
    trailerVideoUrl?: string;
  };
  submissionId?: string | null;
  titleId?: string | null;
  showAssets?: boolean;
  showSubtitles?: boolean;
  showVideoStatus?: boolean;
}

/**
 * Converts formData to a Submission-like shape for consistent rendering
 * This allows us to reuse ViewSubmissionSections components for both data sources
 */
function formDataToSubmission(
  formData: ContentReviewDisplayProps["formData"],
  submissionId?: string | null,
  titleId?: string | null,
): Partial<Submission> {
  if (!formData) return {} as Partial<Submission>;

  // Normalize language field: formData uses 'language' (string | string[]) but Submission uses 'languages' (string[])
  let normalizedLanguages: string[] = [];
  if (Array.isArray(formData.language)) {
    normalizedLanguages = formData.language;
  } else if (formData.language) {
    normalizedLanguages = [formData.language];
  } else if (formData.languages) {
    // Fallback to deprecated languages field if present
    normalizedLanguages = formData.languages;
  }

  const result = {
    id: submissionId || titleId || "",
    title: formData.title || "",
    releaseYear: formData.releaseYear,
    year: formData.releaseYear,
    runtime: formData.runtime,
    durationMinutes: formData.runtime,
    synopsis: formData.synopsis,
    description: formData.description,
    director: formData.director,
    directorData: formData.director ? [{ name: formData.director }] : undefined,
    cast: formData.cast,
    castData: formData.cast?.map((name) => ({ name })),
    crewData: formData.crew,
    genres: formData.genres,
    genreData: formData.genres,
    rating: formData.rating,
    languages: normalizedLanguages,
    country: formData.country,
    posterUrl: formData.posterUrl,
    backdropUrl: formData.backdropUrl,
    logoUrl: formData.logoUrl,
    horizontalTileUrl: formData.horizontalTileUrl,
    verticalTileUrl: formData.verticalTileUrl,
    status: "DRAFT",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  } as Partial<Submission>;

  return result;
}

// Helper function to get video status display
function getVideoStatus(videoUrl?: string) {
  if (!videoUrl) {
    return <span className="text-red-400 text-sm">Not uploaded</span>;
  }
  if (videoUrl.startsWith("pending:")) {
    return <span className="text-blue-400 text-sm">Encoding Pending</span>;
  }
  return <span className="text-green-400 text-sm">✓ Ready</span>;
}

/**
 * Shared component for displaying content review in both
 * ReviewStep (provider/admin editing) and ReviewSubmissionPage (admin approval)
 */
export function ContentReviewDisplay({
  submission,
  formData,
  submissionId,
  titleId,
  showAssets = false,
  showSubtitles = false,
  showVideoStatus = false,
}: Readonly<ContentReviewDisplayProps>): ReactElement {
  const [viewMode, setViewMode] = useState<"data" | "preview">("data");

  // Handle view mode change with transition
  const handleViewModeChange = (mode: "data" | "preview") => {
    if ("startViewTransition" in document) {
      (document as Document & { startViewTransition: (callback: () => void) => void }).startViewTransition(() => {
        setViewMode(mode);
      });
    } else {
      setViewMode(mode);
    }
  };

  // Convert formData to submission shape for consistent rendering
  // Pass submissionId/titleId so SubmissionImage can construct correct R2 URLs
  const displayData =
    submission || (formData ? (formDataToSubmission(formData, submissionId, titleId) as Submission) : null);

  // Convert to preview data
  const previewData = submission
    ? submissionToPreview(submission as Parameters<typeof submissionToPreview>[0])
    : formDataToPreview(formData || {});

  return (
    <div className="space-y-6">
      {/* View Mode Toggle */}
      <div className="flex items-center gap-4 p-1 w-fit">
        <button
          type="button"
          onClick={() => handleViewModeChange("data")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            viewMode === "data"
              ? "text-lumi-accent border border-lumi-accent bg-lumi-accent/20"
              : "bg-none border border-neutral-600 text-neutral-600 hover:text-white"
          }`}
        >
          Data View
        </button>
        <button
          type="button"
          onClick={() => handleViewModeChange("preview")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            viewMode === "preview"
              ? "text-lumi-accent border border-lumi-accent bg-lumi-accent/20"
              : "bg-none border border-neutral-600 text-neutral-600 hover:text-white"
          }`}
        >
          Preview
        </button>
      </div>

      {/* Content area with view transition */}
      <div className="review-mode-transition">
        {/* Preview Mode */}
        {viewMode === "preview" && (
          <div className="space-y-4">
            <div className="rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-400">Title Page Preview</p>
                  <p className="text-sm text-blue-300 mt-1">
                    This is how the title will appear to viewers once published.
                  </p>
                </div>
              </div>
            </div>
            <TitlePreview data={previewData} submissionId={submissionId} titleId={titleId} />

            {/* Card Previews - Horizontal and Vertical Rails */}
            <div className="mt-8 border-t border-neutral-800 pt-8">
              <div className="rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-400">Collection Rail Previews</p>
                    <p className="text-sm text-blue-300 mt-1">
                      See how your title cards appear in horizontal and vertical collection rails.
                    </p>
                  </div>
                </div>
              </div>
              <CardPreviews data={previewData} submissionId={submissionId} titleId={titleId} />
            </div>
          </div>
        )}

        {/* Data View Mode */}
        {viewMode === "data" && (
          <div className="space-y-12">
            {/* Use ViewSubmissionSections components for both submission and formData */}
            {displayData && (
              <>
                <BasicInfoSection submission={displayData} />
                <ContentDetailsSection submission={displayData} />
                <CreditsSection submission={displayData} />
                <VisualAssetsSection submission={displayData} submissionId={submissionId} titleId={titleId} />
              </>
            )}

            {/* Videos - Only show if requested (formData only) */}
            {!submission && showVideoStatus && formData && (
              <div className="border-t border-neutral-800 pt-12">
                <h2 className="text-lg font-semibold text-white mb-4">Videos</h2>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-neutral-500">Feature Film</p>
                    {getVideoStatus(formData?.featureVideoUrl)}
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-neutral-500">Trailer</p>
                    {getVideoStatus(formData?.trailerVideoUrl)}
                  </div>
                </div>
              </div>
            )}

            {/* Additional sections only for full submission view */}
            {submission && showAssets && submission.assets && submission.assets.length > 0 && (
              <div className="border-t border-neutral-800 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Uploaded Assets</h3>
                <div className="space-y-2">
                  {submission.assets.map((asset) => (
                    <div key={asset.id} className="bg-neutral-800/0 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-neutral-500 font-medium">{asset.kind}</span>
                        <span className="text-sm px-2 py-1 rounded bg-green-600/20 text-green-400">
                          {asset.encodingStatus}
                        </span>
                      </div>
                      {asset.rawFileName && <div className="text-sm text-white mt-1">File: {asset.rawFileName}</div>}
                      {asset.contentType && (
                        <div className="text-xs text-neutral-400 mt-0.5">Type: {asset.contentType}</div>
                      )}
                      {asset.uploadedBy && (
                        <div className="text-xs text-neutral-400 mt-0.5">Uploaded by: {asset.uploadedBy}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {submission && showSubtitles && (
              <>
                {(submission.featureSubtitles || submission.trailerSubtitles) && (
                  <div className="border-t border-neutral-800 pt-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Subtitles</h3>
                    {/* Subtitle display would go here */}
                    <p className="text-sm text-neutral-400">
                      {submission.featureSubtitles?.length || 0} feature subtitle(s),{" "}
                      {submission.trailerSubtitles?.length || 0} trailer subtitle(s)
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
